import React, { useEffect } from 'react';
import { get } from 'lodash-es';
import { FormField, Input } from '@amzn/awsui-components-console';
import { Controller, useFormContext } from 'react-hook-form';
import { DiffContent, ViewContent, useEditOnChange, useIsEditContentDisabled } from './contentHelpers';
import { DisplayMode, ResourcePath, ResourceType, iContent } from './contentInterfaces';
import { LabeledContentSkeleton } from '../skeletons/LabeledContentSkeleton';
import { HelpPanelInfoLink } from '..';
import { ContentErrorWrapper } from 'src/components/error/ContentErrorBoundary';
import { DataAttributes, filterDataAttributes, getContentDataAttributes } from 'src/commons/dataAttributes';
import { Rules } from '.';
import { getNodeText } from 'src/commons';

/** Renders the field identified by the `path` as a Polaris `<Input>` component. Manipulates the value as a `string` type
 * For an overview of `<Content>` components, see the [README.md](./README.md). For usage examples, see the [How-to guide](./HowToGuide.md) */
export const TextContent = ContentErrorWrapper(_TextContent);

function _TextContent<RType extends keyof ResourceType, DisabledOnPaths extends ResourcePath<RType>[]>(
	props: iContent<RType, DisabledOnPaths>
) {
	// Processed at top level component to avoid unneccessary executions of a relatively heavy functions between renders
	const dataAttributes = { ...filterDataAttributes(props), ...getContentDataAttributes('Text', props) };

	switch (props.mode) {
		case DisplayMode.Diff:
			return <DiffContent {...props} dataAttributes={dataAttributes} />;
		case DisplayMode.View:
			return <ViewContent {...props} dataAttributes={dataAttributes} />;
		case DisplayMode.Edit:
			return <EditTextContent {...props} dataAttributes={dataAttributes} />;
		case DisplayMode.Loading:
			return <LabeledContentSkeleton label={props.label} />;
		default:
			return null;
	}
}

/** The Edit mode of a particular Text field, renders a `react-hook-form` controlled `<Input>` */
function EditTextContent<RType extends keyof ResourceType, DisabledOnPaths extends ResourcePath<RType>[]>(
	props: iContent<RType, DisabledOnPaths> & { dataAttributes: DataAttributes }
) {
	const { control, clearErrors, setValue } = useFormContext<ResourceType[RType]>();
	const disabled = useIsEditContentDisabled(props);

	return (
		<Controller
			control={control}
			name={props.path}
			rules={Rules.useRules(props.rules, props.label, disabled)}
			render={({ field, formState, fieldState }) => {
				const onChange = useEditOnChange(props, field.onChange, setValue, 'value');
				useEffect(() => {
					// This effect will ignore any potential validation errors if the particular field is disabled
					if (disabled) {
						clearErrors(props.path);
					}
				}, [disabled, formState.isSubmitting, formState.isValidating]);

				return (
					<FormField
						label={props.label}
						description={props.editDescription}
						errorText={get(formState, `errors.${props.path}.message`)}
						info={props.infoHelpPanel && <HelpPanelInfoLink helpPanel={props.infoHelpPanel} />}
					>
						<span {...props.dataAttributes}>
							<Input
								data-testid={getNodeText(props.label)}
								value={field.value as string}
								ref={field.ref}
								onChange={onChange}
								onBlur={field.onBlur}
								invalid={fieldState.invalid}
								disabled={disabled}
								placeholder={props.placeholder}
							/>
						</span>
					</FormField>
				);
			}}
		/>
	);
}
